import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as ChevronIcon } from '../../../assets/svg/chevron.svg';
import { linkButtonSx, outlinedButtonSx, textButtonSx } from '../../login/styles';

export interface SendLinkViewProps {
  defaultEmail?: string;
  isSubmitting?: boolean;
  onSubmit: (email: string) => void | Promise<void>;
}

const solidButtonSx = {
  py: 1.5,
  textTransform: 'none',
  borderRadius: 0,
  backgroundColor: '#070707',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#333',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E6E3DF',
    color: '#B1AEAB',
    opacity: 1,
  },
} as const;

const SendLinkView = ({
  defaultEmail = '',
  isSubmitting = false,
  onSubmit,
}: SendLinkViewProps): React.ReactElement => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [email, setEmail] = useState(defaultEmail);
  const canSubmit = email.trim().length > 0 && !isSubmitting;

  const handleBackToLogin = () => {
    const params = new URLSearchParams(search);
    params.delete('email');
    const query = params.toString();
    navigate(query ? `/login?${query}` : '/login');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    onSubmit(email.trim());
  };

  return (
    <Box width="100%" component="form" onSubmit={handleSubmit}>
      <Stack spacing={2} width="100%">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            aria-label="Back to sign in"
            onClick={handleBackToLogin}
            disabled={isSubmitting}
            sx={{
              color: '#070707',
              ml: -1,
              alignSelf: 'flex-start',
            }}
          >
            <ChevronIcon style={{ transform: 'rotate(180deg)', width: 20, height: 20 }} />
          </IconButton>
        </Box>

        <Stack spacing={2} alignItems="center" width="100%">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Satoshi, sans-serif',
              fontWeight: 500,
              fontSize: '28px',
              lineHeight: '40px',
              letterSpacing: 0,
              textAlign: 'center',
              color: '#070707',
            }}
          >
            Forgot your password?
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            Please enter your email address and we&apos;ll send you a link to reset your password.
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={2} width="100%" sx={{ mt: '40px' }}>
        <TextField
          id="reset-email"
          label="Email"
          type="email"
          autoComplete="email"
          fullWidth
          value={email}
          disabled={isSubmitting}
          onChange={(e) => setEmail(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
            },
          }}
        />
      </Stack>

      <Box sx={{ mt: '156px' }}>
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          loading={isSubmitting}
          disabled={!canSubmit}
          sx={solidButtonSx}
        >
          Reset Password
        </LoadingButton>
      </Box>
    </Box>
  );
};

export interface SubmittedViewProps {
  email: string;
  isSending?: boolean;
  onBack: () => void;
  onResendLink: () => void | Promise<void>;
  onCallSupport: () => void;
}

const SubmittedView = ({
  email,
  isSending = false,
  onBack,
  onResendLink,
  onCallSupport,
}: SubmittedViewProps): React.ReactElement => (
  <Box
    display="flex"
    flexGrow={1}
    flexDirection="column"
    justifyContent="space-between"
  >
    <Stack spacing={2} width="100%">
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton
          aria-label="Back"
          onClick={onBack}
          disabled={isSending}
          sx={{
            color: '#070707',
            ml: -1,
            alignSelf: 'flex-start',
          }}
        >
          <ChevronIcon style={{ transform: 'rotate(180deg)', width: 20, height: 20 }} />
        </IconButton>
      </Box>

      <Typography
        component="h1"
        sx={{
          fontFamily: 'Satoshi, sans-serif',
          fontWeight: 500,
          fontSize: '28px',
          lineHeight: '40px',
          letterSpacing: 0,
          textAlign: 'center',
          color: '#070707',
        }}
      >
        Check Your Email
      </Typography>

      <Typography color="text.secondary" textAlign="center">
        We sent a reset link to
        {' '}
        <strong>{email}</strong>
      </Typography>

      <br />

      <Typography color="text.secondary" textAlign="center">
        Open the email from Zicasso to create a new password.
        Didn&apos;t get it? Check your spam or
        {' '}
        <Box
          component="button"
          type="button"
          onClick={onResendLink}
          disabled={isSending}
          sx={{
            ...linkButtonSx,
            fontSize: 'inherit',
            lineHeight: 'inherit',
          }}
        >
          Send a New Link
        </Box>
      </Typography>
    </Stack>

    <Stack spacing={2} width="100%">
      <Button variant="outlined" fullWidth onClick={onBack} sx={outlinedButtonSx}>
        Back to Sign In
      </Button>
      <Button variant="text" fullWidth onClick={onCallSupport} sx={textButtonSx}>
        Need Help? Call us at 888 441-2418.
      </Button>
    </Stack>
  </Box>
);

export { SendLinkView, SubmittedView };
